import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

function safeUpper(s) {
  return (s || '').toString().trim().toUpperCase();
}

function getPurchaseDocTypeFromNumber(invoiceNumber) {
  const raw = (invoiceNumber || '').toString().trim();
  // Examples: FC2026/001  |  FC 2026/001
  const m = raw.match(/^([A-Za-z]{1,6})\s*\d{2,4}\s*\//);
  if (m && m[1]) return safeUpper(m[1]);
  const m2 = raw.match(/^([A-Za-z]{1,6})\s*/);
  if (m2 && m2[1]) return safeUpper(m2[1]);
  return 'FC';
}

function filterLayoutsByKind(layouts, { kind, docType }) {
  const dt = safeUpper(docType);
  const list = Array.isArray(layouts) ? layouts : [];

  const isAllowed = (l) => {
    const ldt = safeUpper(l?.documentType);
    const name = (l?.name || '').toString().toLowerCase();

    if (ldt === dt || ldt === 'ALL') return true;

    if (kind === 'sales') {
      const salesTypes = new Set(['FT', 'FR', 'FS', 'NC', 'ND', 'RC', 'OR', 'ORC', 'PP', 'GT', 'ALL']);
      if (l?.activeForInvoice) return true;
      if (salesTypes.has(ldt)) return true;
      return false;
    }

    if (kind === 'purchase') {
      const purchaseTypes = new Set(['FC', 'GR', 'ALL']);
      if (purchaseTypes.has(ldt)) return true;
      if (name.includes('compra') || name.includes('fornecedor')) return true;
      return false;
    }

    if (kind === 'stock') {
      const stockTypes = new Set(['MS', 'INV', 'ALL']);
      return stockTypes.has(ldt);
    }

    return false;
  };

  return list.filter(isAllowed).sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
}

function pickDefaultLayoutId({ defaults, docType, layouts }) {
  const dt = safeUpper(docType);

  const row = (Array.isArray(defaults) ? defaults : []).find(x => safeUpper(x?.documentType) === dt);
  if (row?.defaultLayoutId) return Number(row.defaultLayoutId);

  const exact = (layouts || []).find(l => safeUpper(l?.documentType) === dt && (l?.isDefault || l?.activeForInvoice));
  if (exact?.id) return Number(exact.id);

  const def = (layouts || []).find(l => l?.isDefault);
  if (def?.id) return Number(def.id);

  const any = (layouts || [])[0];
  return any?.id ? Number(any.id) : null;
}

export default function DocumentPrintOptionsModal({ open, isDarkMode, context, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [layouts, setLayouts] = useState([]);
  const [defaults, setDefaults] = useState([]);

  const [copies, setCopies] = useState(1);
  const [layoutId, setLayoutId] = useState('');
  const [doPrint, setDoPrint] = useState(true);
  const [doEmail, setDoEmail] = useState(false);
  const [doPdf, setDoPdf] = useState(false);
  const [doSms, setDoSms] = useState(false);
  const [email, setEmail] = useState('');
  const [smsMessage, setSmsMessage] = useState('');

  const kind = context?.kind || 'sales'; // sales | purchase | stock
  const docId = context?.docId;
  const rawDocType = context?.docType;
  const docTitle = context?.title || 'Documento';

  const docType = useMemo(() => {
    if (kind === 'purchase') return safeUpper(rawDocType) || getPurchaseDocTypeFromNumber(context?.invoiceNumber);
    return safeUpper(rawDocType) || 'FT';
  }, [kind, rawDocType, context]);

  const filteredLayouts = useMemo(() => filterLayoutsByKind(layouts, { kind, docType }), [layouts, kind, docType]);

  const theme = useMemo(() => {
    if (isDarkMode) {
      return {
        bg: 'rgba(0,0,0,0.65)',
        card: '#0f172a',
        border: '#24324f',
        text: '#e5e7eb',
        text2: 'rgba(229,231,235,0.75)',
        inputBg: '#111827',
      };
    }
    return {
      bg: 'rgba(17,24,39,0.45)',
      card: '#ffffff',
      border: '#e5e7eb',
      text: '#0f172a',
      text2: 'rgba(15,23,42,0.75)',
      inputBg: '#ffffff',
    };
  }, [isDarkMode]);

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      setLoading(true);
      try {
        const [layRes, defRes] = await Promise.all([
          api.get('/billing/layouts').catch(() => ({ data: [] })),
          api.get('/billing/document-defaults').catch(() => ({ data: [] })),
        ]);

        const l = Array.isArray(layRes.data) ? layRes.data : [];
        const d = Array.isArray(defRes.data) ? defRes.data : [];
        setLayouts(l);
        setDefaults(d);

        const filtered = filterLayoutsByKind(l, { kind, docType });
        const defLayoutId = pickDefaultLayoutId({ defaults: d, docType, layouts: filtered });
        setLayoutId(defLayoutId ? String(defLayoutId) : '');

        setCopies(1);
        setDoPrint(true);
        setDoEmail(false);
        setDoPdf(false);
        setDoSms(false);
        setEmail('');
        setSmsMessage('');
      } catch (e) {
        console.error(e);
        toast.error('Erro ao carregar opções de impressão');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open]);

  if (!open) return null;

  const runActions = async () => {
    if (!docId) {
      toast.error('Documento inválido');
      return;
    }

    const lid = layoutId ? Number(layoutId) : null;
    const c = Math.max(1, Number(copies) || 1);

    setLoading(true);
    try {
      // PRINT (HTML)
      if (doPrint) {
        let url = '';
        if (kind === 'sales') {
          url = `/billing/invoices/${docId}/print?copies=${encodeURIComponent(c)}${lid ? `&layoutId=${encodeURIComponent(lid)}` : ''}`;
        } else if (kind === 'purchase') {
          url = `/billing/purchases/${docId}/print?copies=${encodeURIComponent(c)}${lid ? `&layoutId=${encodeURIComponent(lid)}` : ''}`;
        } else if (kind === 'stock') {
          url = `/warehouse/stock-movements/${docId}/print?copies=${encodeURIComponent(c)}${lid ? `&layoutId=${encodeURIComponent(lid)}` : ''}`;
        }

        if (url) {
          const win = window.open(url, '_blank', 'noopener,noreferrer');
          if (win) {
            setTimeout(() => {
              try { win.print(); } catch { /* ignore */ }
            }, 700);
          }
        }
      }

      // PDF
      if (doPdf) {
        let pdfUrl = '';
        if (kind === 'sales') {
          pdfUrl = `/billing/invoices/${docId}/pdf?copies=${encodeURIComponent(c)}${lid ? `&layoutId=${encodeURIComponent(lid)}` : ''}`;
        } else if (kind === 'purchase') {
          pdfUrl = `/billing/purchases/${docId}/pdf?copies=${encodeURIComponent(c)}${lid ? `&layoutId=${encodeURIComponent(lid)}` : ''}`;
        } else if (kind === 'stock') {
          pdfUrl = `/warehouse/stock-movements/${docId}/pdf?copies=${encodeURIComponent(c)}${lid ? `&layoutId=${encodeURIComponent(lid)}` : ''}`;
        }

        if (pdfUrl) {
          const res = await api.get(pdfUrl, { responseType: 'blob' });
          const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
          window.open(blobUrl, '_blank', 'noopener,noreferrer');
        }
      }

      // EMAIL
      if (doEmail) {
        if (kind === 'stock') {
          toast.info('Email não disponível para documentos de stock');
        }
        if (kind === 'sales') {
          await api.post(`/billing/invoices/${docId}/send-email${lid ? `?layoutId=${encodeURIComponent(lid)}&copies=${encodeURIComponent(c)}` : `?copies=${encodeURIComponent(c)}`}`, email ? { email } : null);
        } else if (kind === 'purchase') {
          await api.post(`/billing/purchases/${docId}/send-email${lid ? `?layoutId=${encodeURIComponent(lid)}&copies=${encodeURIComponent(c)}` : `?copies=${encodeURIComponent(c)}`}`, email ? { email } : null);
        }
        toast.success('📧 Email enviado');
      }

      // SMS
      if (doSms) {
        if (kind === 'stock') {
          toast.info('SMS não disponível para documentos de stock');
        }
        if (kind === 'sales') {
          await api.post(`/billing/sales/invoices/${docId}/send-sms`, smsMessage ? { message: smsMessage } : {});
        } else if (kind === 'purchase') {
          await api.post(`/billing/purchases/${docId}/send-sms`, smsMessage ? { message: smsMessage } : {});
        }
        toast.success('📱 SMS enviado');
      }

      onDone?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Erro ao executar ações');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: theme.bg, zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 'min(860px, 96vw)', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 16, color: theme.text }}>🖨️ Opções de Impressão</div>
            <div style={{ fontSize: 12, color: theme.text2 }}>{docTitle} • {docType} • ID {docId}</div>
          </div>
          <button onClick={() => { onClose?.(); onDone?.(); }} disabled={loading} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 10, cursor: 'pointer', fontWeight: 800 }}>
            ✕ Cancelar
          </button>
        </div>

        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, color: theme.text2, marginBottom: 10 }}>
            Documento gravado. Pretende imprimir/enviar agora?
          </div>

          <div style={{ border: `1px solid ${theme.border}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, padding: 12, borderBottom: `1px solid ${theme.border}`, alignItems: 'center' }}>
              <div style={{ fontWeight: 800, fontSize: 12, color: theme.text2 }}>Nº de Vias</div>
              <div style={{ fontWeight: 800, fontSize: 12, color: theme.text2 }}>Layout</div>

              <div>
                <input
                  type="number"
                  min={1}
                  value={copies}
                  onChange={e => setCopies(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: `1px solid ${theme.border}`, background: theme.inputBg, color: theme.text }}
                />
              </div>

              <div>
                <select
                  value={layoutId}
                  onChange={e => setLayoutId(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: `1px solid ${theme.border}`, background: theme.inputBg, color: theme.text }}
                >
                  <option value="">(Automático)</option>
                  {filteredLayouts.map(l => (
                    <option key={l.id} value={String(l.id)}>{l.name} ({l.documentType || 'All'})</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: theme.text2, marginTop: 4 }}>
                  Mostra layouts compatíveis com este tipo de documento.
                </div>
              </div>
            </div>

            <div style={{ padding: 12, display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', color: theme.text, fontWeight: 700 }}>
                <input type="checkbox" checked={doPrint} onChange={e => setDoPrint(e.target.checked)} />
                Imprimir
              </label>
              {kind !== 'stock' && (
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', color: theme.text, fontWeight: 700 }}>
                  <input type="checkbox" checked={doEmail} onChange={e => setDoEmail(e.target.checked)} />
                  Enviar para Email
                </label>
              )}
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', color: theme.text, fontWeight: 700 }}>
                <input type="checkbox" checked={doPdf} onChange={e => setDoPdf(e.target.checked)} />
                Exportar para PDF
              </label>
              {kind !== 'stock' && (
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', color: theme.text, fontWeight: 700 }}>
                  <input type="checkbox" checked={doSms} onChange={e => setDoSms(e.target.checked)} />
                  Enviar SMS
                </label>
              )}
            </div>

            {kind !== 'stock' && (doEmail || doSms) && (
              <div style={{ padding: 12, borderTop: `1px solid ${theme.border}`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, color: theme.text, marginBottom: 6 }}>Email (opcional)</div>
                  <input
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="(vazio = usa email do cliente/fornecedor)"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: `1px solid ${theme.border}`, background: theme.inputBg, color: theme.text }}
                    disabled={!doEmail}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, color: theme.text, marginBottom: 6 }}>Mensagem SMS (opcional)</div>
                  <input
                    value={smsMessage}
                    onChange={e => setSmsMessage(e.target.value)}
                    placeholder="(vazio = mensagem por defeito)"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 10, border: `1px solid ${theme.border}`, background: theme.inputBg, color: theme.text }}
                    disabled={!doSms}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '12px 16px', borderTop: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: theme.text2 }}>
            Dica: pode sempre imprimir/exportar mais tarde.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => { onClose?.(); onDone?.(); }} disabled={loading} style={{ background: 'transparent', color: theme.text, border: `1px solid ${theme.border}`, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontWeight: 900 }}>
              Não, agora não
            </button>
            <button onClick={runActions} disabled={loading} style={{ background: '#22c55e', color: '#fff', border: 'none', padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontWeight: 900 }}>
              {loading ? 'A processar...' : 'OK'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
